import { useRef } from "react";
import { PAGE_X, PAGE_Y } from "../_shared/safeArea";
import { useTrackedScreen } from "../../hooks/useTrackedScreen";
import type { SchemeTokens } from "@shared/types/foyer";
import { tannManifest } from "@shared/schemes/manifests";
import { SchemeProvider } from "../_shared/schemeContext";
import { useScreenFocus } from "../_shared/useFocusGrid";
import { useBackHome } from "../_shared/useBackHome";
import { LauncherIcon } from "../_shared/launcher/icons";
import {
  LauncherPage,
  LauncherCustom,
  LauncherTv,
  useHomeNav,
  useLauncherClock,
  visibleFunctions,
  homeBgLayers,
  useGuestName,
  welcomeLine,
  SchemeSlot,
  launcherSlotStyle,
  type LauncherTheme,
  type LauncherScreen,
} from "../_shared/launcher/kit";
import { useNow } from "../_shared/useNow";
import { offerSlotBanner } from "../_shared/slots/offerSlot";
import type { SchemeProps } from "../registry";

/**
 * Tann - alpine retreat. ARCHETYPE: a persistent top nav bar over a full-bleed
 * photo (home, with a "Now on TV" lower-third) or the active sub-page on a
 * solid forest surface. Deep forest green + amber, Marcellus / Manrope.
 */
function tannTheme(t: SchemeTokens): LauncherTheme & { rootBg: string } {
  const e = { ...tannManifest.defaultTokens.extras, ...t.extras };
  return {
    serif: t.fonts.serif ?? "",
    sans: t.fonts.sans ?? "",
    surface: e.surface,
    card: e.card,
    chip: e.chip,
    line: t.palette.rule ?? "",
    ink: t.palette.ink,
    muted: t.palette.inkDim ?? "",
    accent: t.palette.accent,
    onAccent: e.onAccent,
    phTone: e.phTone,
    phInk: e.phInk,
    photoTone: "dark",
    rootBg: t.palette.bg,
  };
}

export default function TannScheme({ scheme }: SchemeProps) {
  const scopeRef = useRef<HTMLDivElement>(null);
  useScreenFocus(scopeRef);
  const [screen, setScreen] = useTrackedScreen<LauncherScreen>("home");
  useBackHome(() => setScreen("home"));
  const T = tannTheme(scheme.tokens);
  const c = useLauncherClock();
  const guest = useGuestName();
  const { hotelName } = scheme.content.branding;
  const hero = scheme.content.images.hero;
  const bgL = homeBgLayers(scheme.content.background?.mode);
  const funcs = visibleFunctions(scheme);
  // The home promo slot, when it holds a live offer (`POS-4`). Resolved here
  // because the dock's focus chain needs to know whether there is a cell to
  // add, and `SchemeSlot` needs to know whether to draw a ring on it - one
  // answer behind both, or the remote and the picture disagree.
  // Read off the scheme directly rather than through `useOfferSlot`: this
  // component body sits OUTSIDE its own `SchemeProvider` (the provider is in
  // the tree it returns), so the context hook has nothing to read here.
  const promoClock = useNow();
  const promoBanner = offerSlotBanner(scheme, "homePromo", promoClock);
  const home = useHomeNav({
    funcs,
    screen,
    go: setScreen,
    scopeRef,
    tvPlayer: scheme.context.tvPlayer,
    headerFirst: false,
    // Last in the chain, and only while there is a live offer: an offer nobody
    // asked for may interrupt the eye, never the remote, so the ring still
    // starts on the dock.
    promo: {
      active: !!promoBanner,
      onPress: () => setScreen("offers"),
    },
  });
  const isHome = screen === "home";
  const cream = T.ink;
  const amber = T.accent;

  return (
    <SchemeProvider value={scheme}>
      <div
        ref={scopeRef}
        style={{
          position: "absolute",
          inset: 0,
          background: T.rootBg,
          color: cream,
          overflow: "hidden",
          fontFamily: T.sans,
          outline: "none",
        }}
      >
        {isHome ? (
          <>
            {bgL.photo && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  backgroundColor: hero ? "#000" : T.phTone,
                  backgroundImage: hero
                    ? `url(${hero})`
                    : `repeating-linear-gradient(135deg, ${T.phInk} 0 1px, transparent 1px 11px)`,
                  backgroundSize: hero ? "cover" : undefined,
                  backgroundPosition: "center",
                }}
              />
            )}
            {bgL.tint && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(180deg, rgba(20,29,23,0.86) 0%, rgba(20,29,23,0.1) 18%, transparent 50%, rgba(20,29,23,0.86) 100%)",
                }}
              />
            )}
          </>
        ) : (
          <div
            style={{ position: "absolute", inset: 0, background: T.surface }}
          />
        )}

        {/* top nav bar (persistent) */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 116,
            padding: `${PAGE_Y - 24}px ${PAGE_X}px 0`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            zIndex: 2,
            borderBottom: isHome ? "none" : `1px solid ${T.line}`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <svg width="42" height="28" viewBox="0 0 64 44" aria-hidden="true">
              <path
                d="M4 42 L24 6 L36 28 L44 16 L60 42 Z"
                fill="none"
                stroke={amber}
                strokeWidth="2"
                strokeLinejoin="round"
              />
            </svg>
            <div
              style={{
                fontFamily: T.serif,
                fontSize: 34,
                letterSpacing: "0.28em",
                paddingLeft: "0.28em",
              }}
            >
              {hotelName.toUpperCase()}
            </div>
          </div>
          {isHome ? (
            <div style={{ display: "flex", gap: 40, alignItems: "center" }}>
              {funcs.map((f, i) => {
                const on = home.funcIdx === i;
                return (
                  <div
                    key={f.key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 11,
                      paddingBottom: 6,
                      borderBottom: on
                        ? `2px solid ${amber}`
                        : "2px solid transparent",
                    }}
                  >
                    <LauncherIcon
                      name={f.icon}
                      size={24}
                      color={on ? amber : cream}
                      stroke={1.6}
                    />
                    <span
                      style={{
                        fontFamily: T.sans,
                        fontSize: 18,
                        fontWeight: 600,
                        letterSpacing: "0.04em",
                        color: on ? amber : cream,
                      }}
                    >
                      {f.label}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <span />
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            {home.headerIcons.map((name, k) => {
              const on = isHome && home.headerIdx === k;
              return (
                <div
                  key={name}
                  style={{
                    padding: 6,
                    borderRadius: 9,
                    display: "flex",
                    background: on ? `${amber}26` : "transparent",
                    border: on ? `1px solid ${amber}` : "1px solid transparent",
                  }}
                >
                  <LauncherIcon
                    name={name}
                    size={22}
                    color={on ? amber : cream}
                  />
                </div>
              );
            })}
            <span style={{ width: 1, height: 22, background: T.muted }} />
            <LauncherIcon name="cloud" size={24} color={cream} />
            <span style={{ fontFamily: T.serif, fontSize: 30 }}>−2°</span>
            <span style={{ width: 1, height: 22, background: T.muted }} />
            <span style={{ fontFamily: T.serif, fontSize: 30 }}>
              {c.time24}
            </span>
          </div>
        </div>

        {/* body */}
        {isHome ? (
          <>
            <div
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                top: 360,
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  letterSpacing: "0.36em",
                  textTransform: "uppercase",
                  color: amber,
                  marginBottom: 16,
                }}
              >
                Alpine Retreat · 1·680m
              </div>
              <div style={{ fontFamily: T.serif, fontSize: 84, lineHeight: 1 }}>
                {welcomeLine("Welcome", guest)}
              </div>
            </div>
            {/* operator promo slot - top-left, under the nav bar */}
            <SchemeSlot
              focused={home.promoFocused}
              onPress={() => setScreen("offers")}
              slotId="homePromo"
              style={launcherSlotStyle(T)}
              containerStyle={{
                position: "absolute",
                top: 150,
                left: PAGE_X,
                width: 420,
                zIndex: 2,
              }}
            />
            <div
              style={{
                position: "absolute",
                left: PAGE_X,
                right: PAGE_X,
                bottom: 64,
                display: "flex",
                alignItems: "center",
                gap: 26,
                padding: "22px 30px",
                borderRadius: 18,
                background: "rgba(20,29,23,0.55)",
                backdropFilter: "blur(10px)",
                border: `1px solid ${T.line}`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <LauncherIcon name="tv" size={34} color={amber} stroke={1.6} />
                <span
                  style={{
                    fontSize: 12,
                    letterSpacing: "0.3em",
                    textTransform: "uppercase",
                    color: amber,
                  }}
                >
                  Now on TV
                </span>
              </div>
              <div style={{ width: 1, height: 40, background: T.line }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: T.serif, fontSize: 30 }}>
                  SVT1 · Aktuellt
                </div>
                <div style={{ fontSize: 15, color: T.muted, marginTop: 3 }}>
                  Evening news · until 21:30
                </div>
              </div>
              <span
                style={{
                  fontSize: 15,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: cream,
                  opacity: 0.85,
                }}
              >
                Press OK to watch
              </span>
            </div>
          </>
        ) : screen === "tv" ? (
          // Full-bleed, before LauncherPage: the built-in player carries its
          // own left-hand menu and must own the whole panel, exactly like the
          // vendor app it replaces.
          <LauncherTv theme={T} go={setScreen} scopeRef={scopeRef} />
        ) : screen === "custom" ? (
          <LauncherCustom theme={T} go={setScreen} scopeRef={scopeRef} />
        ) : (
          <div
            style={{
              position: "absolute",
              top: 116,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          >
            <LauncherPage
              screen={screen}
              theme={T}
              go={setScreen}
              scopeRef={scopeRef}
            />
          </div>
        )}
      </div>
    </SchemeProvider>
  );
}
